import { Dialog } from "../../../../common/core-ui/Dialog.tsx";
import { FormattedMessage, useIntl } from "react-intl";
import styles from "./Help.module.scss";
import BaseLinkOrButton from "../../../../common/core-ui/BaseLinkOrButton.tsx";
import {
  MissingBooksIcon,
  MyOrderIcon,
  NewOrderIcon,
  NewIcon,
  SupportIcon,
} from "./helpIcons.tsx";
import CaretsSmallRight from "../../../../common/icons/CaretsSmallRight.tsx";
import MiscellaneousAccessibility from "../../../../common/icons/MiscellaneousAccessibility.tsx";
import MiscellaneousBook from "../../../../common/icons/MiscellaneousBook.tsx";
import MiscellaneousSearch from "../../../../common/icons/MiscellaneousSearch.tsx";
import BookshelfSearchContent from "../../../../common/icons/BookshelfSearchContent.tsx";
import SocialHeart from "../../../../common/icons/SocialHeart.tsx";
import { useBrandLoaderData } from "../../../root/rootLoader.ts";
import { useHelp } from "./useHelp.ts";
import useHasAccountCenterResources from "../../layout/sidebar/my-account/useHasAccountCenterResources.ts";
import MiscellaneousRefresh from "../../../../common/icons/MiscellaneousRefresh.tsx";
import { virtualStoreReturnPolicyUrl } from "../../product/common/productUtils.ts";

function HelpInner({ onUpdateLibrary }: { onUpdateLibrary: () => void }) {
  const intl = useIntl();
  const brand = useBrandLoaderData();
  const helpInfo = useHelp();
  const hasAccountCenterResources = useHasAccountCenterResources();

  const boxLinks = [
    {
      label: intl.formatMessage({ defaultMessage: "What's New" }),
      icon: <NewIcon />,
      href: "https://bookshelfsupport.vitalsource.com/hc/en-us/articles/32271334714903-What-s-new-with-Bookshelf-Online",
      enabled: helpInfo.hasWhatsNew,
    },
    {
      label: intl.formatMessage({ defaultMessage: "Missing Books" }),
      icon: <MissingBooksIcon />,
      href: helpInfo.missingBooksURL,
      enabled: helpInfo.hasMissingBooks,
    },
    {
      label: intl.formatMessage({
        defaultMessage: "I need help with my order",
      }),
      icon: <MyOrderIcon />,
      href: "https://onlinebookstoresupport.vitalsource.com/hc/en-us/categories/32643164976279-Frequently-Asked-Questions",
      enabled: helpInfo.isVirtualStore,
    },
    {
      label: intl.formatMessage({
        defaultMessage: "I need help with placing a new order",
      }),
      icon: <NewOrderIcon />,
      href: "https://onlinebookstoresupport.vitalsource.com/hc/en-us/categories/32643164976279-Frequently-Asked-Questions",
      enabled: helpInfo.isVirtualStore,
    },
    {
      label:
        brand.Support.label ??
        intl.formatMessage({ defaultMessage: "Support" }),
      icon: <SupportIcon />,
      href: helpInfo.supportURL,
      enabled: helpInfo.hasGeneralSupport || helpInfo.isVirtualStore,
    },
  ].filter((el) => el.enabled);

  const topicLinks = [
    {
      label: intl.formatMessage({ defaultMessage: "Shipping" }),
      href: brand.VirtualStore.Shipping.policyUrl,
      enabled: helpInfo.isVirtualStore,
    },
    {
      label: intl.formatMessage({ defaultMessage: "Returns" }),
      href: virtualStoreReturnPolicyUrl,
      enabled: helpInfo.isVirtualStore,
    },
    {
      label: intl.formatMessage({ defaultMessage: "Rentals" }),
      href: "https://onlinebookstoresupport.vitalsource.com/hc/en-us/articles/28516014701207",
      enabled: helpInfo.isVirtualStore,
    },
    {
      label: intl.formatMessage({ defaultMessage: "Redeem a code" }),
      href: "https://bookshelfsupport.vitalsource.com/hc/en-us/articles/32271526153623-How-to-Redeem-a-VitalSource-Redemption-Code",
      enabled: brand.RedeemCode.enabled && !helpInfo.isVirtualStore,
    },
    {
      label: intl.formatMessage({
        defaultMessage: "Access Codes / Courseware",
      }),
      href: helpInfo.accessCodesURL,
      enabled: hasAccountCenterResources,
    },
    {
      label: intl.formatMessage({ defaultMessage: "Buy Back" }),
      href: "https://onlinebookstoresupport.vitalsource.com/hc/en-us/articles/28516044099479",
      enabled: helpInfo.isVirtualStore,
    },
    {
      label: intl.formatMessage({ defaultMessage: "Read books offline" }),
      href: "https://bookshelfsupport.vitalsource.com/hc/en-us/articles/33611977681431-How-do-I-access-the-offline-or-downloadable-version-of-my-book",
      enabled: brand.DownloadOffline.enabled,
    },
  ].filter((el) => el.enabled);

  const featureLinks = [
    {
      label: intl.formatMessage({ defaultMessage: "My Library" }),
      href: "https://bookshelfsupport.vitalsource.com/hc/en-us/articles/32273540399767-Bookshelf-Home-My-Library",
      icon: <MiscellaneousBook />,
      enabled: true,
    },
    {
      label: intl.formatMessage({ defaultMessage: "Title Search" }),
      href: "https://bookshelfsupport.vitalsource.com/hc/en-us/articles/32272629504023-Bookshelf-Home-Searching-your-Bookshelf-Library#h_01HR5CTRC99296JZZ4031S7JKK",
      icon: <MiscellaneousSearch />,
      enabled: brand.Search.enabled,
    },
    {
      label: intl.formatMessage({ defaultMessage: "Content Search" }),
      href: "https://bookshelfsupport.vitalsource.com/hc/en-us/articles/32272629504023-Bookshelf-Home-Searching-your-Bookshelf-Library#h_01HR5CTRC99296JZZ4031S7JKK",
      icon: <BookshelfSearchContent />,
      enabled: brand.Search.enabled,
    },
    {
      label: intl.formatMessage({ defaultMessage: "Favorites" }),
      href: "https://bookshelfsupport.vitalsource.com/hc/en-us/articles/32273566602775-Bookshelf-Home-Favorites",
      icon: <SocialHeart />,
      enabled: true,
    },
  ].filter((el) => el.enabled);

  return (
    <div className={styles.container}>
      {boxLinks.length > 0 && (
        <ul className={styles.boxLinks}>
          {boxLinks.map((link) => (
            <li key={link.label}>
              <BaseLinkOrButton className={styles.boxLink} href={link.href}>
                {link.icon}
                {link.label}
              </BaseLinkOrButton>
            </li>
          ))}
        </ul>
      )}
      <div className={styles.accessibilityStatement}>
        <BaseLinkOrButton
          className="vst-link vst-link-option-no-underline"
          href="https://www.vitalsource.com/accessibility-statement"
        >
          <MiscellaneousAccessibility />
          <FormattedMessage defaultMessage="Accessibility Statement" />
        </BaseLinkOrButton>
      </div>
      {helpInfo.hasTopicBreakdown && (
        <>
          <div className={styles.popularTopics}>
            <div className={styles.popularTopicsHeaderRow}>
              <h3>
                <FormattedMessage defaultMessage="Popular Topics" />
              </h3>
              <BaseLinkOrButton className="vst-link" href={helpInfo.supportURL}>
                <FormattedMessage defaultMessage="View All" />
                <CaretsSmallRight />
              </BaseLinkOrButton>
            </div>
            <ul>
              {topicLinks.map((link) => (
                <li key={link.label}>
                  <BaseLinkOrButton className="vst-link" href={link.href}>
                    {link.label}
                  </BaseLinkOrButton>
                </li>
              ))}
            </ul>
          </div>
          <div className={styles.featuresArea}>
            <h3>
              {helpInfo.isVirtualStore ? (
                <FormattedMessage defaultMessage="How to use these helpful tools" />
              ) : (
                <FormattedMessage defaultMessage="Learn more about our features" />
              )}
            </h3>
            <ul>
              {featureLinks.map((link, i) => (
                <li key={link.label}>
                  <BaseLinkOrButton
                    className="vst-icon-button-circle"
                    href={link.href}
                    aria-labelledby={`help-link-${i}`}
                  >
                    {link.icon}
                  </BaseLinkOrButton>
                  <div id={`help-link-${i}`}>{link.label}</div>
                </li>
              ))}
            </ul>
          </div>
        </>
      )}
      {brand.UpdateLicense.enabled && (
        <div className={styles.updateLibrary}>
          <BaseLinkOrButton
            onClick={onUpdateLibrary}
            className="vst-link vst-link-option-no-underline"
          >
            <MiscellaneousRefresh />
            <FormattedMessage defaultMessage="Update Library" />
          </BaseLinkOrButton>
        </div>
      )}
    </div>
  );
}

export default function Help({
  onRequestClose,
  onUpdateLibrary,
}: {
  onRequestClose: () => void;
  onUpdateLibrary: () => void;
}) {
  const intl = useIntl();

  return (
    <Dialog
      onRequestClose={onRequestClose}
      darkHeader
      title={intl.formatMessage({ defaultMessage: "Help" })}
    >
      <HelpInner onUpdateLibrary={onUpdateLibrary} />
    </Dialog>
  );
}
